import { FalloDeVisor } from "./errores.js";

export interface AvanceDescarga {
  /** Bytes recibidos. */
  recibidos: number;
  /** Total segun Content-Length, o null si el servidor no lo manda. */
  total: number | null;
}

export type EscuchaAvance = (avance: AvanceDescarga) => void;

/**
 * Descarga el .frag informando avance real.
 *
 * Se lee `Response.body` como ReadableStream y se compara contra `Content-Length`
 * en vez de usar el evento de progreso de XHR. Si el servidor no manda
 * `Content-Length` (respuesta comprimida en el borde, por ejemplo), `total` queda en
 * null y la UI muestra una barra indeterminada en lugar de mentir con un porcentaje.
 */
export async function descargarModelo(
  url: string,
  onAvance: EscuchaAvance,
  senal: AbortSignal,
): Promise<Uint8Array> {
  let respuesta: Response;
  try {
    respuesta = await fetch(url, { signal: senal, cache: "no-store" });
  } catch (causa) {
    throw new FalloDeVisor("fallo_de_red", causa);
  }

  if (respuesta.status === 403 || respuesta.status === 401) {
    // La URL prefirmada caduco entre que la API la emitio y el visor la uso.
    throw new FalloDeVisor("link_expirado");
  }
  if (respuesta.status === 404) throw new FalloDeVisor("modelo_no_encontrado");
  if (!respuesta.ok) throw new FalloDeVisor("fallo_de_red");

  const cabecera = respuesta.headers.get("Content-Length");
  const total = cabecera === null ? null : Number(cabecera);
  const totalValido = total !== null && Number.isFinite(total) && total > 0 ? total : null;

  const cuerpo = respuesta.body;
  if (cuerpo === null) throw new FalloDeVisor("fallo_de_red");

  const trozos: Uint8Array[] = [];
  let recibidos = 0;
  const lector = cuerpo.getReader();

  try {
    for (;;) {
      const { done, value } = await lector.read();
      if (done) break;
      trozos.push(value);
      recibidos += value.byteLength;
      onAvance({ recibidos, total: totalValido });
    }
  } catch (causa) {
    throw new FalloDeVisor("fallo_de_red", causa);
  } finally {
    lector.releaseLock();
  }

  if (recibidos === 0) throw new FalloDeVisor("modelo_corrupto");
  if (totalValido !== null && recibidos !== totalValido) {
    // Conexion cortada a media descarga: el buffer parcial reventaria en el worker.
    throw new FalloDeVisor("modelo_corrupto");
  }

  const bytes = new Uint8Array(recibidos);
  let offset = 0;
  for (const trozo of trozos) {
    bytes.set(trozo, offset);
    offset += trozo.byteLength;
  }
  return bytes;
}
